import React from 'react';
import {H1} from "../../components/titles/titles";
import styles from "./SmsFileScreen.module.css";
import FieldsPane, {FieldsCol} from "../../components/FieldsPane/FieldsPane";
import Field from "../../components/Field/Field";
import ItemNoResults from "../../components/ItemNoResults/ItemNoResults";
import moment from "moment";
import SmsFileStatus from "../../components/SmsFileStatus/SmsFileStatus";
import ActionTile from "../../components/ActionTileBar/ActionTile";
import ActionTileBar from "../../components/ActionTileBar/ActionTileBar";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import SmsFileContentsTable from "./SmsFileContentsTable";

type Props = {
    smsFile: SmsFileType | null | undefined;
    onEdit: (() => void) | null;
    onDownloadOriginalFile: (() => void) | null;
    onDownloadProcessedFile: (() => void) | null;
    onProcessFile: (() => void) | null;
    onCancel: (() => void) | null;
};

const SmsFileScreen = ({
                           smsFile,
                           onEdit,
                           onDownloadOriginalFile,
                           onDownloadProcessedFile,
                           onProcessFile,
                           onCancel,
                       }: Props) => {
    return (
        <div>
            <div className={styles.titleWrapper}>
                <H1>Sms file</H1>
            </div>
            {smsFile && (
                <SmsFileScreenContents
                    smsFile={smsFile}
                    onEdit={onEdit}
                    onDownloadOriginalFile={onDownloadOriginalFile}
                    onDownloadProcessedFile={onDownloadProcessedFile}
                    onProcessFile={onProcessFile}
                    onCancel={onCancel}
                />)}
            {smsFile === null && <ItemNoResults/>}
        </div>
    );
};

type SmsFileScreenContentsProps = {
    smsFile: SmsFileType;
    onEdit: (() => void) | null;
    onDownloadOriginalFile: (() => void) | null;
    onDownloadProcessedFile: (() => void) | null;
    onProcessFile: (() => void) | null;
    onCancel: (() => void) | null;
};

const SmsFileScreenContents = ({
                                   smsFile,
                                   onEdit,
                                   onDownloadOriginalFile,
                                   onDownloadProcessedFile,
                                   onProcessFile,
                                   onCancel,
                               }: SmsFileScreenContentsProps) => {
    const {
        dateCreated,
        mailWhenComplete,
        reference,
        beneficiary,
        documentType,
        dueDate,
        expiryDate,
        messageNL,
        messageFR,
        messageEN,
        rowsComplete,
        status,
        validateLog,
        processLog,
        processTimestamp,
        userName,
    } = smsFile;

    return (
        <>
            <div className={styles.primaryFieldsWrapper}>
                <FieldsPane>
                    <FieldsCol>
                        <Field label="Date created" children={moment(dateCreated).format('DD/MM/YYYY HH:mm')}/>
                        {processTimestamp && <Field label="Date completed" children={moment(processTimestamp).format('DD/MM/YYYY HH:mm')}/>}
                        <Field label="Created by" children={userName}/>
                        <Field label="Send status mails" children={mailWhenComplete ? 'Yes' : 'No'}/>
                        <Field label="Rows complete" children={rowsComplete}/>
                    </FieldsCol>
                    <FieldsCol>
                        <div className={styles.statusRow}>
                            <SmsFileStatus status={status}/>
                        </div>
                        <SmsFileProgressBar smsFile={smsFile}/>
                    </FieldsCol>
                </FieldsPane>
            </div>
            <div className={styles.fieldsWrapper}>
                <FieldsPane>
                    <FieldsCol>
                        <Field label="Reference" children={reference}/>
                        <Field label="Document type" children={documentType}/>
                        {documentType !== 'Contribution' && <Field label="Due date" children={moment(dueDate).format('DD/MM/YYYY HH:mm')}/>}
                        <Field label="Expiration date" children={moment(expiryDate).format('DD/MM/YYYY HH:mm')}/>
                    </FieldsCol>
                    <FieldsCol>
                        <Field label="Beneficiary" children={beneficiary}/>
                        <div className={styles.smsMessageWrapper}><Field label="Message (NL)" children={messageNL}/></div>
                        <div className={styles.smsMessageWrapper}><Field label="Message (FR)" children={messageFR}/></div>
                        <div className={styles.smsMessageWrapper}><Field label="Message (EN)" children={messageEN}/></div>
                    </FieldsCol>
                </FieldsPane>
            </div>
            <div className={styles.titleWrapper}>
                <H1>Contents</H1>
            </div>
            <div className={styles.fileContentsWrapper}>
                <SmsFileContentsTable file={smsFile}/>
            </div>
            <div className={styles.titleWrapper}>
                <H1>Actions</H1>
            </div>
            <div className={styles.actionBarWrapper}>
                <ActionTileBar>
                    {onEdit && (
                        <ActionTile
                            onClick={onEdit}
                            title="Edit"
                            icon="iconEdit"
                        />
                    )}
                    {onDownloadOriginalFile && (
                        <ActionTile
                            onClick={onDownloadOriginalFile}
                            title="Download original file"
                            icon="iconDownloadOriginal"
                        />
                    )}
                    {onDownloadProcessedFile && (
                        <ActionTile
                            onClick={onDownloadProcessedFile}
                            title="Download processed file"
                            icon="iconDownloadProcessed"
                        />
                    )}
                    {onProcessFile && (
                        <ActionTile
                            onClick={onProcessFile}
                            title="Process file"
                            icon="iconProcess"
                        />
                    )}
                    {onCancel && (
                        <ActionTile
                            onClick={onCancel}
                            title="Cancel"
                            icon="iconCancel"
                        />
                    )}
                </ActionTileBar>
            </div>
            {Boolean(validateLog?.length || processLog?.length) && (
                <>
                    <div className={styles.titleWrapper}>
                        <H1>Log</H1>
                    </div>
                    <div className={styles.fieldsWrapper}>
                        <FieldsPane>
                            <ul className={styles.list}>
                                {[...(validateLog || []), ...(processLog || [])].map((logItem, i) => (
                                    <li key={i}>{logItem}</li>
                                ))}
                            </ul>
                        </FieldsPane>
                    </div>
                </>
            )}
        </>
    );
};

type SmsFileProgressBarProps = {
    smsFile: SmsFileType;
}

const SmsFileProgressBar = ({smsFile}: SmsFileProgressBarProps) => {
    const {
        status,
        totalRows,
        rowsComplete,
    } = smsFile;

    if (!["VALIDATING", "RUNNING"].includes(status)) {
        return null;
    }

    let widthPercentage = 5; //Always show some progress

    if (totalRows && totalRows > 0 && rowsComplete) {
        widthPercentage = rowsComplete / totalRows * 100;
        widthPercentage = Math.min(widthPercentage, 100);
    }

    return (
        <div className={styles.progressBarContainer}>
            <ProgressBar widthPercentage={widthPercentage}/>
        </div>
    );
};

export default SmsFileScreen;
